import { PriceDisplay } from '@chart/types/axis.types';
import { BaseChartAPIProps } from '@chart/types/create.types';
import { ResampleIntervals } from '@chart/types/resample.types';
import { getTimefromSeries } from '@chart/utils/timeseries/time-series';
import { getCurrentYIndex, getPercentage } from '@chart/utils/y-scale/y-scale';
import { isPriceSnake } from '@helpers';
import { addMinutes, differenceInMilliseconds, startOfMinute } from 'date-fns';

import { isMergedAxis } from '../axis/axis';
import { drawCrosshair } from './crosshair';

export const getNextBoundaryUTC = (
  date: Date,
  interval: ResampleIntervals
): Date => {
  if (interval !== '1m') {
    return date;
  }

  return startOfMinute(addMinutes(date, 1));
};

export const drawLiveCrosshair = (base: BaseChartAPIProps) => {
  const primaryPane = base.panes.find(p => p.id === base.primaryPaneId);

  if (!primaryPane) {
    return;
  }

  // The x scale is based on base.primaryAsset (asset with longest history)
  // Find the most recent time among all assets for alignment
  const primaryAsset = base.primaryAsset;
  const primaryAssetInPane =
    primaryPane.chartAssetData.find(
      asset => asset.entity.id === primaryAsset.entity.id
    ) || primaryPane.chartAssetData[0];
  const primaryAssetTs = primaryAssetInPane.ts;

  if (!primaryAssetInPane || primaryAssetTs.length === 0) {
    return;
  }

  let mostRecentTime = primaryAssetTs[primaryAssetTs.length - 1].time;
  let mostRecentAsset = primaryAssetInPane;
  const primaryAssetData = primaryPane.chartAssetData;

  primaryAssetData.forEach(asset => {
    if (!asset.isHidden && asset.ts.length > 0) {
      const lastBar = asset.ts[asset.ts.length - 1];
      if (lastBar.time > mostRecentTime) {
        mostRecentTime = lastBar.time;
        mostRecentAsset = asset;
      }
    }
  });

  const primaryLastIdx = primaryAssetTs.length - 1;
  const isMerged = isMergedAxis(primaryPane.yAxisType);

  const targetLastIdx = primaryLastIdx;
  const targetTime = mostRecentTime;
  const { domainTimeSeries, seriesType } = primaryPane;
  const { bars, threshold, getThemeColor } = base.options.colors;

  const hoveredPoints = primaryAssetData
    .map((asset, i) => {
      const assetTs = asset.ts;
      if (asset.isHidden || assetTs.length === 0) {
        return null;
      }

      const axisIndex = getCurrentYIndex({
        isAxisMerged: isMerged,
        pane: primaryPane,
        asset,
      });

      const elementYScale = primaryPane.y[axisIndex];
      const baseValue = domainTimeSeries[i]?.start.close;

      if (!elementYScale || baseValue === undefined) {
        return null;
      }

      let timeseriesIdx: number;

      if (asset === mostRecentAsset) {
        timeseriesIdx = assetTs.length - 1;
      } else {
        // For other assets, match onMouseMove pattern: normalize the primary asset's time
        const normalizedTime = getTimefromSeries(
          isPriceSnake(asset.entity.default_snake),
          targetTime
        );
        timeseriesIdx = asset.tsByTime.get(normalizedTime) ?? -1;

        // Only draw if asset has data at this time point (same as onMouseMove)
        if (timeseriesIdx === -1) {
          return null;
        }
      }

      const value = assetTs[timeseriesIdx];
      const previousValue = assetTs[timeseriesIdx - 1] || value;

      const priceDisplay: PriceDisplay = isMerged ? 'percentage' : 'price';
      const pointValue =
        priceDisplay === 'price'
          ? value.close
          : getPercentage(value.close, baseValue);
      const isBullish = value.close >= previousValue.close;

      return {
        value: pointValue,
        circleColor: getThemeColor(asset.lineColorToken),
        lineColor: isBullish ? bars.up.value : bars.down.value,
        backgroundColor: isBullish
          ? threshold.positive.value
          : threshold.negative.value,
        y: elementYScale,
        yCoord: elementYScale.yScale(pointValue),
        priceDisplay,
        shouldDrawCircle: seriesType === 'line' || asset !== mostRecentAsset,
        yAxisSize: base.yAxisSizes[axisIndex],
      };
    })
    .filter((point): point is NonNullable<typeof point> => point !== null);

  if (hoveredPoints.length === 0) {
    return;
  }

  let subtitle;

  if (base.resampleInterval === '1m') {
    const nextBoundaryFromBar = getNextBoundaryUTC(
      new Date(mostRecentTime),
      base.resampleInterval
    );
    const remainingMs = Math.max(
      0,
      differenceInMilliseconds(nextBoundaryFromBar, new Date())
    );
    const secs = Math.max(0, Math.ceil(remainingMs / 1000));
    subtitle = `0:${secs.toString().padStart(2, '0')}`;
  }

  drawCrosshair({
    ...base,
    x1: base.x.xScale(targetLastIdx),
    drawVertical: false,
    date: new Date(mostRecentTime),
    subtitle,
    hoveredPoints,
  });
};
